import { OriasReturnable, PAGES } from "@dune/types";
import { getErrorMessage } from "@dune/vars";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useOriasAndSirenContext } from "../../context/oriasAndSirenContext";
import { publicAxios } from "../axios";
import { useMutateGetSocietyInfoFromSiren } from "../queries/siren.queries";

const getOneOriasDetailsByNumber = async (oriasNumber: string) => {
	const { data } = await publicAxios.get<OriasReturnable>(
		`/orias/search/societies/${oriasNumber}`,
	);
	return data;
};

export const useGetOneOriasDetailsByNumberForRegister = () => {
	const navigate = useNavigate();
	const { setOriasData, setSirenData } = useOriasAndSirenContext();
	const getSocietyInfo = useMutateGetSocietyInfoFromSiren();

	return useMutation({
		mutationFn: getOneOriasDetailsByNumber,
		onSuccess: async (data) => {
			setOriasData(data);
			const validCategories = data.registrations.filter(
				(reg) =>
					(reg.categoryName === "COA" || reg.categoryName === "AGA") &&
					reg.status !== "SUPPRIME",
			);

			if (validCategories.length === 0) {
				throw new Error(
					"L'Orias doit être de catégorie COA ou AGA, et ne pas avoir été supprimé.",
				);
			}

			try {
				const siren = data.informationBase.siren;
				const clientSocietyData = await getSocietyInfo.mutateAsync(siren);
				if (clientSocietyData) setSirenData(clientSocietyData);
			} catch {
				console.error("Erreur lors de l'appel de l'API SIREN :");
			}

			navigate(PAGES.REGISTER_BROKER_MAIN_DETAILS);
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Une erreur est survenue", {
				description: errorMessage,
			});
		},
	});
};

const getOneOriasDetailsByOneNumber = async (oriasNumber: string) => {
	const { data } = await publicAxios.get<OriasReturnable>(
		`/orias/search/${oriasNumber}`,
	);
	return data;
};

export const useGetOriasDetails = (oriasNumber: string, enabled = true) =>
	useQuery<OriasReturnable, AxiosError<{ message: string }>>({
		queryKey: ["orias-details", oriasNumber],
		queryFn: () => getOneOriasDetailsByOneNumber(oriasNumber),
		enabled: !!oriasNumber && enabled,
	});
